import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SwitchAccessShortcutAddIcon from '@mui/icons-material/SwitchAccessShortcutAdd';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridFilterItem,
  GridGroupingColDefOverride,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { AsyncDialog } from '../../components/AsyncDialog';
import { AsyncDialogRef } from '../../components/AsyncDialog/AsyncDialog';
import { FakeButtonLink, GridActionsCellItemLink } from '../../components/FakeLink';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { userPortfolioRequestStatusToInfo } from '../../constants/tables/userPortfolioRequest';
import { OWNER_TYPE_TAG_COLORS, OwnerTypeTag } from '../../constants/treasury';
import { usePermissions } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import { createPortfolioOrder } from '../../redux/modules/portfolioOrder/portfolioOrder.actions';
import { portfolioOrderSelector } from '../../redux/modules/portfolioOrder/portfolioOrder.selectors';
import {
  fetchUserPortfolioRequests,
  updateUserPortfolioRequestStatus,
} from '../../redux/modules/userPortfolioRequest/userPortfolioRequest.actions';
import {
  userPortfolioRequestByRequestIdSelector,
  userPortfolioRequestSelector,
} from '../../redux/modules/userPortfolioRequest/userPortfolioRequest.selectors';
import { ReducerState } from '../../redux/store.types';
import { locations } from '../../routes/locations';
import { FundCategory } from '../../types/indexFund';
import {
  UserPortfolioRequest,
  UserPortfolioRequestStatus,
  UserPortfolioRequestType,
} from '../../types/userPortfolioRequest';
import { round2Format } from '../../utils/number';
import * as permissions from '../../utils/permissions';
import { getOwnerTypeTag } from '../../utils/treasury';
import { CreateOrderConfirmationDialog } from './CreateOrderConfirmationDialog';
import { productTypeSelector } from '../../redux/modules/productType/product-type.selectors';
import { fetchProductTypes } from '../../redux/modules/productType/product-type.actions';

interface UserPortfolioRequestTableProps {
  type: UserPortfolioRequestType;
  setProducts: (ps) => any;
  setFundCategories: (fcs) => any;
  setStatuses: (ss) => any;
  externalFilter?: any[];
  /** Held false until the page has restored its saved filters. */
  fetchEnabled?: boolean;
}

export function UserPortfolioRequestTable({
  type,
  setProducts,
  setFundCategories,
  setStatuses,
  externalFilter = [],
  fetchEnabled,
}: UserPortfolioRequestTableProps) {
  const requestId = `portfolio-order-list-${type}`;
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('apiError');
  const tableRef = useRef<TableRef>(null);
  const asyncDialogRef = useRef<AsyncDialogRef>(null);
  const [selectedRow, setSelectedRow] = useState<UserPortfolioRequest>();
  const [actionMenu, setActionMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([
    // { ...GRID_CHECKBOX_SELECTION_COL_DEF, disableReorder: true, hideable: false },
    {
      field: 'id',
      headerName: 'Id',
      width: 50,
    },
    {
      field: 'type.name',
      headerName: 'Type',
      type: 'singleSelect',
      valueOptions: Object.values(UserPortfolioRequestType),
      width: 180,
      valueGetter: (params) => params.row?.type?.name,
      groupingValueGetter: (params) => params.row?.type?.name,
    },
    {
      field: 'fundCategory.name',
      headerName: 'Fund Category',
      type: 'singleSelect',
      valueOptions: Object.values(FundCategory),
      width: 130,
      valueGetter: (params) => params.row?.fundCategory?.name,
      groupingValueGetter: (params) => params.row?.fundCategory?.name,
    },
    {
      field: 'fundSource.name',
      headerName: 'Fund Source',
      type: 'string',
      width: 130,
      valueGetter: (params) => params.row?.fundSource?.name,
    },
    {
      field: 'ownerType',
      headerName: 'Owner Type',
      type: 'singleSelect',
      valueOptions: Object.values(OwnerTypeTag),
      width: 100,
      valueGetter: (params) => {
        // Handle auto-generated group rows (e.g. "265 (1)") by deriving owner type from first child row
        if (`${params.id}`.startsWith('auto-generated-row-userPortfolio.id/')) {
          if (tableRef.current && 'children' in params.rowNode) {
            const rows: UserPortfolioRequest[] = (params.rowNode.children || []).map(tableRef.current.getApi().getRow);

            return getOwnerTypeTag(rows?.[0]?.userPortfolio?.subscribedPortfolio?.ownerType?.name);
          }
        }

        const requestType = params.row?.type?.name as UserPortfolioRequestType | undefined;

        // Subscriptions and redemptions: from the subscribed portfolio's owner type (treasury, employee or user)
        if (
          requestType === UserPortfolioRequestType.SUBSCRIPTION ||
          requestType === UserPortfolioRequestType.REDEMPTION
        ) {
          return getOwnerTypeTag(params.row?.userPortfolio?.subscribedPortfolio?.ownerType?.name);
        }

        // Default other types to B2C
        return OwnerTypeTag.B2C;
      },
      renderCell: (params: GridRenderCellParams) => {
        const value = (params.value as OwnerTypeTag) || OwnerTypeTag.B2C;
        const color = OWNER_TYPE_TAG_COLORS[value] ?? OWNER_TYPE_TAG_COLORS[OwnerTypeTag.B2C];

        return <Chip label={value} color={color} size="small" variant="outlined" />;
      },
    },
    {
      field: 'userPortfolio.fundAmount',
      headerName: 'Amount',
      type: 'number',
      width: 170,
      valueGetter: (params) => {
        if (`${params.id}`.startsWith('auto-generated-row-userPortfolio.id/')) {
          if (tableRef.current && 'children' in params.rowNode) {
            const rows: UserPortfolioRequest[] = (params.rowNode.children || []).map(tableRef.current.getApi().getRow);

            const amount = rows.reduce((res, upr) => res + (upr.changes?.categoryAmount || 0), 0);

            return `${round2Format(amount)}/${round2Format(rows?.[0]?.userPortfolio?.fundAmount || 0)}`;
          }
        }

        if (params.row?.type?.name === UserPortfolioRequestType.REDEMPTION) {
          return params.row?.changes?.redeemAll ? 'all' : params.row?.changes?.amount;
        }

        return params.row?.changes?.categoryAmount ?? params.row?.userPortfolio?.fundAmount;
      },
      valueFormatter: ({ value, id }) => {
        if (!value || `${id}`.startsWith('auto-generated-row-userPortfolio.id/') || value === 'all') {
          return value;
        }

        return round2Format(value);
      },
    },
    {
      field: 'userPortfolio.userId',
      headerName: 'User',
      type: 'number',
      width: 120,
      valueGetter: (params) => params.row?.userPortfolio?.userId,
      groupingValueGetter: (params) => params.row?.userPortfolio?.userId,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }

        return (
          <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.user(params.value)}>
            {params.value}
          </FakeButtonLink>
        );
      },
    },
    {
      field: 'userPortfolio.id',
      headerName: 'Request id',
      type: 'number',
      width: 120,
      hideable: false,
      valueGetter: (params) => params.row?.userPortfolio?.id,
      groupingValueGetter: (params) => params.row?.userPortfolio?.id,
    },
    {
      field: 'userPortfolio.subscribedPortfolio.productType.nameEn',
      headerName: 'Portfolio',
      type: 'string',
      width: 300,
      valueGetter: (params) => {
        if (params.row?.id) {
          return `(${params.row?.userPortfolio?.subscribedPortfolio?.id}) ${params.row?.userPortfolio?.subscribedPortfolio?.productType?.nameEn}`;
        }
        return '';
      },
      groupingValueGetter: (params) => params.row?.userPortfolio?.subscribedPortfolio?.nameEn,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return;
        }

        return (
          <Tooltip title={params.value} followCursor>
            <FakeButtonLink
              startIcon={<OpenInNewIcon />}
              href={locations.subscribedPortfolio(params.row?.userPortfolio?.subscribedPortfolio?.id)}
            >
              {params.value}
            </FakeButtonLink>
          </Tooltip>
        );
      },
    },
    // {
    //   field: 'userPortfolio.cacheAllocationValue',
    //   headerName: 'Cache Allocation',
    //   type: 'number',
    //   width: 150,
    //   valueGetter: (params) => params.row?.userPortfolio?.cacheAllocationValue,
    // },
    // {
    //   field: 'userPortfolio.cacheAllocationPercentage',
    //   headerName: 'Cache Allocation %',
    //   type: 'number',
    //   width: 150,
    //   valueGetter: (params) => params.row?.userPortfolio?.cacheAllocationPercentage,
    // },
    {
      field: 'status.name',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: Object.values(UserPortfolioRequestStatus),
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => params.row?.status?.name,
      renderCell: (params: GridRenderCellParams) => {
        const info = userPortfolioRequestStatusToInfo[params.row?.status?.name as UserPortfolioRequestStatus] || {};
        const Icon = info?.icon;

        if (!Icon) {
          return params.row?.status?.name;
        }

        return <Chip variant="outlined" color={info?.color} icon={<Icon />} label={params.row?.status?.name} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
  ]);
  const canDoActions = usePermissions({ items: [permissions.UserPortfolioRequest.statusUpdate] });
  const { updateStatusStatus } = useSelector(userPortfolioRequestSelector);
  const { totalUserPortfolioRequests, userPortfolioRequests, fetchStatus } = useSelector((state: ReducerState) =>
    userPortfolioRequestByRequestIdSelector(state, requestId),
  );
  const { createStatus: portfolioOrderCreateStatus } = useSelector(portfolioOrderSelector);

  const { productTypes } = useSelector(productTypeSelector);

  const updateStatusLoading = updateStatusStatus === fetchStatuses.pending;
  const loading = fetchStatus === fetchStatuses.pending || portfolioOrderCreateStatus === fetchStatuses.pending;
  // The API only accepts a rejection while the request itself is still open. Offering Reject on a
  // settled row produced a guaranteed failed call, which is what the "admin cannot reject" report
  // was. It cannot be decided here whether a SIBLING request blocks the rejection — that check
  // needs the whole portfolio — so those still surface as RELATED_REQUEST_BLOCKS_REJECTION.
  const rejectableStatuses: UserPortfolioRequestStatus[] = [
    UserPortfolioRequestStatus.PENDING,
    UserPortfolioRequestStatus.WAITING_CAST_APPROVAL,
  ];
  const availableStatuses = (
    [UserPortfolioRequestStatus.CANCELED, UserPortfolioRequestStatus.REJECTED] as UserPortfolioRequestStatus[]
  ).filter((s) => {
    if (s === selectedRow?.status?.name) {
      return false;
    }

    return s === UserPortfolioRequestStatus.REJECTED
      ? !!selectedRow && rejectableStatuses.includes(selectedRow.status.name)
      : true;
  });
  const firstSelectedItem = userPortfolioRequests.find((item) => item.id === selectedIds?.[0]);
  // TODO: Find a way to store this
  // const [initialFilterModel, setInitialFilterModel] = useSearchParamsState('filter', search.get('filter') || '');
  // const [initialPage, setInitialPage] = useSearchParamsState('page', search.get('page') || '0');
  // const [initialPageSize, setInitialPageSize] = useSearchParamsState('pageSize', search.get('pageSize') || '10');

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, data: UserPortfolioRequest) => {
    setSelectedRow(data);
    setActionMenu(
      actionMenu === null
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
          }
        : null,
    );
  };

  const isRowSelectable = (params: GridRowParams<UserPortfolioRequest>): boolean =>
    params.row?.status?.name === UserPortfolioRequestStatus.PENDING &&
    (!firstSelectedItem ||
      (params.row?.fundCategory?.name === firstSelectedItem?.fundCategory?.name &&
        params.row?.changes?.redeemAll === firstSelectedItem?.changes?.redeemAll));

  const handleCloseMenu = () => {
    setActionMenu(null);
  };

  const updateStatus = async (id: number, status: UserPortfolioRequestStatus) => {
    handleCloseMenu();

    try {
      await dispatch(updateUserPortfolioRequestStatus({ id, status })).unwrap();

      tableRef?.current?.reload();
      enqueueSnackbar('User Portfolio Request status updated!', { variant: 'success' });
    } catch (error: any) {
      // Redux Toolkit serializes a thrown error down to name/message/stack/code, so `data` is
      // always undefined here and stringifying it only appended the word "undefined" to the toast.
      enqueueSnackbar(`User Portfolio Request update status error: ${t(error?.message)}`, { variant: 'error' });
    }
  };

  const onCreateOrder = async () => {
    if (!selectedIds.length) {
      return;
    }

    const isConfirm = await asyncDialogRef?.current?.show();

    if (!isConfirm) {
      return;
    }

    try {
      const {
        data: { orderId, executionId },
      } = await dispatch(createPortfolioOrder({ userPortfolioRequestIds: selectedIds })).unwrap();

      tableRef?.current?.reload();
      enqueueSnackbar('Portfolio Order created!', {
        variant: 'success',
        action: (
          <Grid container flexDirection="row">
            <Grid item>
              <Divider orientation="vertical" color="#fff" />
            </Grid>
            <Grid item>
              <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.portfolioOrder(orderId)} color="inherit">
                Order
              </FakeButtonLink>
            </Grid>
            <Grid item>
              <FakeButtonLink
                startIcon={<OpenInNewIcon />}
                href={locations.orderExecution(executionId)}
                color="inherit"
              >
                Execution
              </FakeButtonLink>
            </Grid>
          </Grid>
        ),
      });
    } catch (error: any) {
      enqueueSnackbar(`Portfolio Order creation error: ${t(error?.message)} ${JSON.stringify(error?.data)}`, {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    dispatch(fetchProductTypes({ limit: 100, offset: 0 }));
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    const products = productTypes.map((product) => product.nameEn);

    const fundCategories = Object.values(FundCategory);

    const statues = Object.values(UserPortfolioRequestStatus);

    setProducts([...products, 'Select All', 'Clear All']);

    setFundCategories([...fundCategories, 'Select All', 'Clear All']);

    setStatuses([...statues, 'Select All', 'Clear All']);
  }, [userPortfolioRequests]);

  useEffect(() => {
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        if (canDoActions && params.row.id) {
          actions.push(
            <GridActionsCellItem
              key={`${params.id}-employee-list-actions-${params.row.id}`}
              icon={<MoreVertIcon />}
              label="Actions"
              onClick={(e) => handleMenuOpen(e, params.row)}
            />,
          );
        }

        if (`${params.id}`.startsWith('auto-generated-row-userPortfolio.id/')) {
          const userPortfolioRequestId = `${params.id}`.substring(`${params.id}`.lastIndexOf('/') + 1);

          actions.push(
            <GridActionsCellItemLink
              key={`${params.id}-details-action`}
              icon={<OpenInNewIcon />}
              label="Details"
              href={locations.userPortfolioRequest(userPortfolioRequestId)}
            />,
          );
        }

        return actions;
      },
    };

    if (canDoActions) {
      setColumns([actionColumn, ...columns]);
    }
  }, [canDoActions]);

  useEffect(() => {
    let ids = selectedIds;
    const firstItem = userPortfolioRequests.find((item) => item.id === ids?.[0]);

    if (firstItem) {
      ids = ids.filter((id) => {
        const fItem = userPortfolioRequests.find((item) => item.id === id);

        return fItem?.fundCategory?.name === firstItem?.fundCategory?.name;
      });
    }

    if (ids.length !== selectedIds.length) {
      setSelectedIds(ids);
    }
  }, [selectedIds]);

  return (
    <>
      <Table
        fetchEnabled={fetchEnabled}
        ref={tableRef}
        fetchItems={fetchUserPortfolioRequests}
        rows={userPortfolioRequests}
        columns={columns}
        loading={loading}
        rowCount={totalUserPortfolioRequests}
        checkboxSelection
        isRowSelectable={isRowSelectable}
        rowSelectionModel={selectedIds}
        onSelectionModelChange={(sm) => setSelectedIds(sm as number[])}
        // onFilterModelChange={(filterModel) => setInitialFilterModel(JSON.stringify(filterModel))}
        // onPageChange={(page) => setInitialPage(`${page}`)}
        // onPageSizeChange={(pageSize) => setInitialPageSize(`${pageSize}`)}
        disableRowGrouping={false}
        rowGroupingColumnMode="single"
        initialRowGroupingModel={['userPortfolio.id']}
        defaultGroupingExpansionDepth={1}
        initialColumnVisibilityModel={{ 'type.name': false, 'userPortfolio.id': false }}
        requiredFilters={[{ field: 'type.name', value: type, operator: 'is', id: 1 }]}
        externalFilter={
          (externalFilter.length
            ? [{ field: 'type.name', value: type, operator: 'is', id: 1 }, ...externalFilter]
            : [{ field: 'type.name', value: type, operator: 'is', id: 1 }]) as GridFilterItem[]
        }
        // initialFilterModel={parseJSON(initialFilterModel)}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        // initialPage={+initialPage}
        // initialPageSize={+initialPageSize}
        sortingMode="client"
        fetchItemsCustomParams={{ requestId }}
        customToolbarItems={[
          <Button
            id="NewOrderButton"
            key="user-portfolio-custom-toolbar-1"
            disabled={!selectedIds.length}
            startIcon={<SwitchAccessShortcutAddIcon />}
            onClick={onCreateOrder}
          >
            New order {!!selectedIds.length && `(${selectedIds.length} requests)`}
          </Button>,
        ]}
        groupingColDef={(params) => {
          const override: GridGroupingColDefOverride = {};

          if (params.fields.includes('userPortfolio.id')) {
            return {
              headerName: 'Request',
              filterable: false,
              sortable: false,
              hideable: false,
            };
          }

          return override;
        }}
      />
      <Menu
        open={actionMenu !== null}
        onClose={handleCloseMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          actionMenu !== null
            ? {
                top: actionMenu.mouseY,
                left: actionMenu.mouseX,
              }
            : undefined
        }
      >
        {availableStatuses.map((status) => {
          const info = userPortfolioRequestStatusToInfo[status] || {};

          return (
            <MenuItem
              key={`user-portfolio-request-list-menu-action-${status}`}
              onClick={() => selectedRow && updateStatus(selectedRow.id, status)}
              disabled={updateStatusLoading}
            >
              {info.menuAction}
            </MenuItem>
          );
        })}
        {!availableStatuses.length && <MenuItem disabled>No actions available for this status</MenuItem>}
      </Menu>
      <AsyncDialog ref={asyncDialogRef} dialog={CreateOrderConfirmationDialog} />
    </>
  );
}
